import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Notice } from '../Toast'
import { Navigate, useNavigate } from 'react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { enrollMfa, getSsoConfig, login, startMfaSetup, verifyMfa } from '../api/endpoints'
import type { LoginResult, MfaSetup } from '../api/types'
import { useAuth } from '../auth/useAuth'
import { homeFor, rolesOf } from '../auth/roles'
import { ErrorNotice } from '../components'
import { SplitLayout, type HeroCopy } from '../Brand'

const HERO: HeroCopy = {
  eyebrow: 'Admissions control room',
  title: 'Every assessment, one calm place.',
  text: 'Open a campaign, watch registrations arrive, review every candidate and send reports.',
  points: ['Campaigns with their own registration link', 'A live view from registration to report', 'An audit trail on every change'],
  chips: ['Registrations +24 today', 'Scoring complete', 'Report ready'],
}

/** The four-square Microsoft mark in its brand colours, shown beside the sign-in button. Decorative. */
function MicrosoftMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  )
}

type Step = { kind: 'password' } | { kind: 'code'; interim: string } | { kind: 'setup'; interim: string }

/** Password first, then a code from an authenticator app. Admin roles must enrol the app at first sign-in. */
export default function LoginPage() {
  const { authed, roles, signIn } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>({ kind: 'password' })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const sso = useQuery({ queryKey: ['sso-config'], queryFn: getSsoConfig, retry: false })
  const ssoError = new URLSearchParams(window.location.search).get('sso_error')

  const finish = (r: LoginResult) => {
    signIn(r.token)
    navigate(homeFor(rolesOf(r.token)), { replace: true })
  }
  const pw = useMutation({
    mutationFn: () => login(email.trim(), password),
    onSuccess: (r) => {
      setPassword('')
      if (r.status === 'OK') finish(r)
      else setStep({ kind: r.status === 'MFA_REQUIRED' ? 'code' : 'setup', interim: r.token })
    },
  })
  const verify = useMutation({ mutationFn: (interim: string) => verifyMfa(interim, code), onSuccess: finish })
  const enroll = useMutation({ mutationFn: (interim: string) => enrollMfa(interim, code), onSuccess: finish })

  if (authed) return <Navigate to={homeFor(roles)} replace />

  const submitPassword = (e: FormEvent) => { e.preventDefault(); pw.mutate() }
  return (
    <SplitLayout hero={HERO}>
    <div className="auth page-enter">
      <h1>Compass Assess · Admin</h1>
      {step.kind === 'password' && (
        <form className="card" onSubmit={submitPassword}>
          <label className="field">Email<input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          <label className="field">Password<input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></label>
          <ErrorNotice error={pw.error} />
          <button className="primary" type="submit" disabled={pw.isPending}>{pw.isPending ? 'Signing in…' : 'Continue'}</button>
          {sso.data?.microsoft && (
            <>
              <p className="muted" style={{ textAlign: 'center' }}>or</p>
              <a className="btn ms-btn" href="/api/admin/auth/sso/microsoft/start"><MicrosoftMark />Sign in with Microsoft</a>
            </>
          )}
          {ssoError && <Notice tone="error">{ssoError === 'cancelled' ? 'Microsoft sign-in was cancelled.' : 'Microsoft sign-in did not work. Your account may not be set up for it. Ask a super admin, or use your password.'}</Notice>}
        </form>
      )}
      {step.kind === 'code' && (
        <form className="card" onSubmit={(e) => { e.preventDefault(); verify.mutate(step.interim) }}>
          <p>Enter the 6-digit code from your authenticator app.</p>
          <CodeInput value={code} onChange={setCode} />
          <ErrorNotice error={verify.error} />
          <button className="primary" type="submit" disabled={verify.isPending || code.length !== 6}>{verify.isPending ? 'Checking…' : 'Sign in'}</button>
          <button type="button" className="link" onClick={() => { setStep({ kind: 'password' }); setCode('') }}>Start again</button>
        </form>
      )}
      {step.kind === 'setup' && (
        <SetupMfa interim={step.interim} code={code} setCode={setCode} onSubmit={() => enroll.mutate(step.interim)} busy={enroll.isPending} error={enroll.error} />
      )}
    </div>
    </SplitLayout>
  )
}

function CodeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="field">6-digit code
      <input type="text" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} required value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))} />
    </label>
  )
}

function SetupMfa({ interim, code, setCode, onSubmit, busy, error }: { interim: string; code: string; setCode: (v: string) => void; onSubmit: () => void; busy: boolean; error: unknown }) {
  const [setup, setSetup] = useState<MfaSetup | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [failed, setFailed] = useState<unknown>(null)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    startMfaSetup(interim).then(setSetup).catch(setFailed)
  }, [interim])
  useEffect(() => {
    if (!setup) return
    // Loaded only on this screen so the QR library is not part of the normal bundle.
    void import('qrcode').then((m) => m.toDataURL(setup.otpauthUri, { margin: 1, width: 192 })).then(setQr).catch(() => setQr(null))
  }, [setup])

  return (
    <form className="card" onSubmit={(e) => { e.preventDefault(); onSubmit() }}>
      <h2>Set up two-step verification</h2>
      <p>Admin accounts need an authenticator app (such as Google Authenticator, Microsoft Authenticator or 1Password).</p>
      <ErrorNotice error={failed} />
      {setup && (
        <>
          {qr && <img src={qr} width={192} height={192} alt="QR code to add this account to your authenticator app" />}
          <p className="muted">Can't scan? Enter this key by hand:</p>
          <p><code data-testid="mfa-secret">{setup.secret}</code></p>
          <CodeInput value={code} onChange={setCode} />
          <ErrorNotice error={error} />
          <button className="primary" type="submit" disabled={busy || code.length !== 6}>{busy ? 'Checking…' : 'Confirm and sign in'}</button>
        </>
      )}
    </form>
  )
}
